import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  BlockStack,
  InlineStack,
  Text,
  Thumbnail,
  Box,
  Divider,
  Banner,
  Button,
} from '@shopify/polaris';
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { ProductBundle, BundleProduct } from '../types';
import { ProductPickerModal, SelectedProduct } from './ProductPickerModal';

interface BundleModalProps {
  open: boolean;
  bundle: ProductBundle | null; // null = create new
  onClose: () => void;
  onSave: (bundle: BundleFormState) => Promise<void>;
  isLoading?: boolean;
  currency?: string;
}

export interface BundleFormState {
  id?: string;
  name: string;
  description: string;
  discountType: 'percentage' | 'fixed_amount' | 'fixed_price';
  discountValue: number;
  bundleType: 'fbt' | 'cross_sell' | 'upsell' | 'custom';
  isActive: boolean;
  products: BundleProduct[];
}

export function BundleModal({ open, bundle, onClose, onSave, isLoading, currency = 'EUR' }: BundleModalProps) {
  const { t } = useTranslation();

  const discountTypeOptions = [
    { label: t('bundles.discountTypePercentage'), value: 'percentage' },
    { label: t('bundles.discountTypeFixed'), value: 'fixed_amount' },
    { label: t('bundles.discountTypePrice'), value: 'fixed_price' },
  ];

  const bundleTypeOptions = [
    { label: t('bundles.bundleTypeFbt'), value: 'fbt' },
    { label: t('bundles.bundleTypeCrossSell'), value: 'cross_sell' },
    { label: t('bundles.bundleTypeUpsell'), value: 'upsell' },
    { label: t('bundles.bundleTypeCustom'), value: 'custom' },
  ];
  const [formState, setFormState] = useState<BundleFormState>({
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: 10,
    bundleType: 'fbt',
    isActive: true,
    products: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Initialize form when bundle changes
  useEffect(() => {
    if (bundle) {
      setFormState({
        id: bundle.id,
        name: bundle.name,
        description: bundle.description || '',
        discountType: bundle.discountType,
        discountValue: bundle.discountValue,
        bundleType: bundle.bundleType,
        isActive: bundle.isActive ?? true,
        products: bundle.products,
      });
    } else {
      setFormState({
        name: '',
        description: '',
        discountType: 'percentage',
        discountValue: 10,
        bundleType: 'custom',
        isActive: true,
        products: [],
      });
    }
    setError(null);
  }, [bundle, open]);

  const handleChange = useCallback((field: keyof BundleFormState, value: string | number | boolean) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  // Add selected products from the picker
  const handleAddProducts = useCallback((selected: SelectedProduct[]) => {
    setFormState(prev => ({
      ...prev,
      products: [
        ...prev.products,
        ...selected.map((p): BundleProduct => ({
          productId: p.productId,
          variantId: p.variantId,
          title: p.title,
          handle: p.handle,
          imageUrl: p.imageUrl,
          price: p.price,
          quantity: p.quantity,
          isAnchor: false,
        })),
      ],
    }));
  }, []);

  // Remove a product from the bundle
  const handleRemoveProduct = useCallback((productId: number) => {
    setFormState(prev => ({
      ...prev,
      products: prev.products.filter(p => p.productId !== productId),
    }));
  }, []);

  // Update product quantity
  const handleQuantityChange = useCallback((productId: number, quantity: number) => {
    setFormState(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.productId === productId ? { ...p, quantity: Math.max(1, quantity) } : p
      ),
    }));
  }, []);

  // Toggle anchor product
  const handleToggleAnchor = useCallback((productId: number) => {
    setFormState(prev => ({
      ...prev,
      products: prev.products.map(p =>
        p.productId === productId ? { ...p, isAnchor: !p.isAnchor } : p
      ),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    // Validation
    if (!formState.name.trim()) {
      setError(t('bundles.errors.nameRequired'));
      return;
    }
    if (formState.discountValue <= 0) {
      setError(t('bundles.errors.discountPositive'));
      return;
    }
    if (formState.discountType === 'percentage' && formState.discountValue > 100) {
      setError(t('bundles.errors.percentageMax'));
      return;
    }
    if (formState.products.length < 2) {
      setError(t('bundles.minProducts'));
      return;
    }

    setError(null);
    await onSave(formState);
  }, [formState, onSave]);

  // Format currency based on shop's currency
  const formatCurrency = (value: number) => {
    const localeMap: Record<string, string> = {
      'EUR': 'es-ES',
      'USD': 'en-US',
      'GBP': 'en-GB',
      'JPY': 'ja-JP',
      'CAD': 'en-CA',
      'AUD': 'en-AU',
      'CHF': 'de-CH',
      'CNY': 'zh-CN',
      'MXN': 'es-MX',
      'BRL': 'pt-BR',
    };
    const locale = localeMap[currency] || 'es-ES';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  // Calculate prices
  const calculateOriginalPrice = () => {
    return formState.products.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  const calculateBundlePrice = () => {
    const original = calculateOriginalPrice();
    switch (formState.discountType) {
      case 'percentage':
        return original * (1 - formState.discountValue / 100);
      case 'fixed_amount':
        return Math.max(0, original - formState.discountValue);
      case 'fixed_price':
        return formState.discountValue;
      default:
        return original;
    }
  };

  const isEditing = !!bundle;
  const title = isEditing ? t('bundles.editBundle') : t('bundles.createBundleTitle');
  const originalPrice = calculateOriginalPrice();
  const bundlePrice = calculateBundlePrice();
  const savings = originalPrice - bundlePrice;

  // Don't allow closing the modal while product picker is open
  const handleClose = useCallback(() => {
    if (!pickerOpen) {
      onClose();
    }
  }, [pickerOpen, onClose]);

  return (
    <>
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      primaryAction={{
        content: isEditing ? t('bundles.saveChanges') : t('bundles.createBundle'),
        onAction: handleSave,
        loading: isLoading,
      }}
      secondaryActions={[
        {
          content: t('common.cancel'),
          onAction: handleClose,
        },
      ]}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          )}

          {/* Show Shopify discount code (read-only) when editing an existing bundle */}
          {bundle?.shopifyDiscountCode && (
            <Banner tone="info">
              <InlineStack gap="200" blockAlign="center">
                <Text as="span" variant="bodyMd" fontWeight="semibold">
                  {t('bundles.shopifyDiscountCode')}:
                </Text>
                <Text as="span" variant="bodyMd">
                  <code style={{ 
                    background: 'var(--p-color-bg-surface-secondary)', 
                    padding: '2px 8px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    letterSpacing: '0.5px'
                  }}>
                    {bundle.shopifyDiscountCode}
                  </code>
                </Text>
              </InlineStack>
            </Banner>
          )}

          <FormLayout>
            <TextField
              label={t('bundles.bundleName')}
              value={formState.name}
              onChange={(value) => handleChange('name', value)}
              autoComplete="off"
              placeholder={t('bundles.bundleNamePlaceholder')}
            />

            <TextField
              label={t('bundles.description')}
              value={formState.description}
              onChange={(value) => handleChange('description', value)}
              autoComplete="off"
              multiline={3}
              placeholder={t('bundles.descriptionPlaceholder')}
            />

            <InlineStack gap="400">
              <Box minWidth="200px">
                <Select
                  label={t('bundles.discountType')}
                  options={discountTypeOptions}
                  value={formState.discountType}
                  onChange={(value) => handleChange('discountType', value)}
                />
              </Box>
              <Box minWidth="150px">
                <TextField
                  label={formState.discountType === 'percentage' ? t('bundles.discountPercent') : t('bundles.discountAmount')}
                  type="number"
                  value={formState.discountValue.toString()}
                  onChange={(value) => handleChange('discountValue', parseFloat(value) || 0)}
                  autoComplete="off"
                  suffix={formState.discountType === 'percentage' ? '%' : undefined}
                  prefix={formState.discountType !== 'percentage' ? '$' : undefined}
                />
              </Box>
            </InlineStack>

            <Select
              label={t('bundles.bundleType')}
              options={bundleTypeOptions}
              value={formState.bundleType}
              onChange={(value) => handleChange('bundleType', value)}
              helpText={t('bundles.bundleTypeHelp')}
            />

            <Checkbox
              label={t('bundles.activeLabel')}
              checked={formState.isActive}
              onChange={(value) => handleChange('isActive', value)}
              helpText={t('bundles.activeHelp')}
            />
          </FormLayout>

          <Divider />

          {/* Products in bundle */}
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">{t('bundles.productsInBundle')}</Text>
              <Button
                icon={PlusIcon}
                onClick={() => setPickerOpen(true)}
              >
                {t('bundles.addProducts')}
              </Button>
            </InlineStack>
            
            {formState.products.length === 0 ? (
              <Banner tone="info">
                {t('bundles.addProductsHelp')}
              </Banner>
            ) : (
              <BlockStack gap="200">
                {formState.products.map((product, index) => (
                  <Box
                    key={index}
                    padding="300"
                    borderRadius="200"
                    background="bg-surface-secondary"
                  >
                    <InlineStack gap="300" align="start" blockAlign="center">
                      <Thumbnail
                        source={product.imageUrl || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'}
                        alt={product.title}
                        size="small"
                      />
                      <Box minWidth="200px">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="semibold" truncate>
                            {product.title}
                          </Text>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {formatCurrency(product.price)} {t('bundles.priceEach')}
                          </Text>
                        </BlockStack>
                      </Box>
                      <InlineStack gap="200" blockAlign="center">
                        <Box minWidth="80px">
                          <TextField
                            label={t('bundles.quantity')}
                            labelHidden
                            type="number"
                            value={product.quantity.toString()}
                            onChange={(value) => handleQuantityChange(product.productId, parseInt(value) || 1)}
                            autoComplete="off"
                            min={1}
                          />
                        </Box>
                        <Checkbox
                          label={t('bundles.anchor')}
                          checked={product.isAnchor}
                          onChange={() => handleToggleAnchor(product.productId)}
                        />
                        <Button
                          icon={DeleteIcon}
                          variant="tertiary"
                          tone="critical"
                          onClick={() => handleRemoveProduct(product.productId)}
                          accessibilityLabel={`Remove ${product.title}`}
                        />
                      </InlineStack>
                    </InlineStack>
                  </Box>
                ))}
              </BlockStack>
            )}
          </BlockStack>

          {formState.products.length > 0 && (
            <>
              <Divider />
              
              {/* Price Summary */}
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">{t('bundles.priceSummary')}</Text>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">{t('bundles.originalPrice')}:</Text>
                  <Text as="span">{formatCurrency(originalPrice)}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">{t('bundles.discount')}:</Text>
                  <Text as="span" tone="success">-{formatCurrency(savings)}</Text>
                </InlineStack>
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" variant="headingMd">{t('bundles.bundlePrice')}:</Text>
                  <Text as="span" variant="headingMd" fontWeight="bold" tone="success">
                    {formatCurrency(bundlePrice)}
                  </Text>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="success">
                  {t('bundles.customersSave')} {formatCurrency(savings)} ({((savings / originalPrice) * 100).toFixed(0)}% {t('bundles.off')})
                </Text>
              </BlockStack>
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>

    {/* Product Picker Modal - must be outside the main modal */}
    <ProductPickerModal
      open={pickerOpen}
      onClose={() => setPickerOpen(false)}
      onSelect={handleAddProducts}
      existingProductIds={formState.products.map(p => p.productId)}
      maxSelection={10 - formState.products.length}
    />
    </>
  );
}
